import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Briefcase, Lock, CheckCircle, XCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const ResetPasswordPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [email, setEmail] = useState('');
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    // Verify token on mount
    useEffect(() => {
        const verifyToken = async () => {
            if (!token) {
                setError('No reset token provided. Please request a new password reset link.');
                setVerifying(false);
                return;
            }

            try {
                const res = await axios.get(`${API}/api/auth/verify-reset-token/${token}`);
                setTokenValid(true);
                setEmail(res.data.email || '');
            } catch (err) {
                setError(err.response?.data?.detail || 'Invalid or expired reset link. Please request a new one.');
                setTokenValid(false);
            } finally {
                setVerifying(false);
            }
        };

        verifyToken();
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        
        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        
        try {
            setLoading(true);
            await axios.post(`${API}/api/auth/reset-password`, {
                token,
                new_password: password
            });
            setSuccess(true);
            toast.success('Password reset successfully!');
        } catch (err) {
            console.error('Reset password error:', err);
            toast.error(err.response?.data?.detail || 'Failed to reset password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Password strength indicator
    const getPasswordStrength = () => {
        if (!password) return { strength: 0, label: '', color: '' };
        let strength = 0;
        if (password.length >= 6) strength++;
        if (password.length >= 8) strength++;
        if (/[A-Z]/.test(password)) strength++;
        if (/[0-9]/.test(password)) strength++;
        if (/[^A-Za-z0-9]/.test(password)) strength++;
        
        if (strength <= 2) return { strength, label: 'Weak', color: 'bg-red-500' };
        if (strength <= 3) return { strength, label: 'Medium', color: 'bg-yellow-500' };
        return { strength, label: 'Strong', color: 'bg-green-500' };
    };

    const passwordStrength = getPasswordStrength();

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#F5EDE5] to-[#E8D5C4] flex items-center justify-center p-4">
            {/* Background Pattern */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#D4BBA6]/30 rounded-full blur-3xl" />
                <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#E8D5C4]/50 rounded-full blur-3xl" />
            </div>

            <div className="relative z-10 w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#4A3728] mb-4 shadow-lg">
                        <Briefcase className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold text-[#4A3728] uppercase tracking-wide">
                        SEVORA TEAM
                    </h1>
                </div>

                <Card className="bg-white/95 backdrop-blur-xl border-[#D4BBA6] shadow-xl">
                    <CardHeader className="text-center pb-4">
                        <CardTitle className="text-[#4A3728] text-xl">
                            {verifying ? 'Verifying...' : 
                             success ? 'Password Reset!' : 
                             tokenValid ? 'Reset Password' : 'Link Invalid'}
                        </CardTitle>
                        <CardDescription className="text-[#5D4A3A]">
                            {verifying ? 'Please wait while we verify your link' :
                             success ? 'Your password has been changed successfully' :
                             tokenValid ? `Create a new password for ${email}` : ''}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {verifying ? (
                            <div className="text-center py-8">
                                <Loader2 className="w-8 h-8 animate-spin text-[#4A3728] mx-auto" />
                                <p className="text-[#5D4A3A] text-sm mt-4">Verifying reset link...</p>
                            </div>
                        ) : success ? (
                            <div className="text-center py-4">
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <CheckCircle className="w-8 h-8 text-green-600" />
                                </div>
                                <p className="text-[#5D4A3A] text-sm mb-6">
                                    You can now login with your new password.
                                </p>
                                <Button 
                                    onClick={() => navigate('/login')}
                                    className="w-full bg-[#4A3728] hover:bg-[#3A2A1E] text-white"
                                    data-testid="go-to-login-btn"
                                >
                                    Go to Login
                                </Button>
                            </div>
                        ) : !tokenValid ? (
                            <div className="text-center py-4">
                                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <XCircle className="w-8 h-8 text-red-600" />
                                </div>
                                <p className="text-[#5D4A3A] text-sm mb-2">{error}</p>
                                <p className="text-[#9C8C74] text-xs mb-6">
                                    Reset links expire after 1 hour for security reasons.
                                </p>
                                <div className="space-y-3">
                                    <Link to="/forgot-password">
                                        <Button 
                                            className="w-full bg-[#4A3728] hover:bg-[#3A2A1E] text-white"
                                        >
                                            Request New Reset Link
                                        </Button>
                                    </Link>
                                    <Link to="/login">
                                        <Button 
                                            variant="outline"
                                            className="w-full border-[#D4BBA6] text-[#4A3728] hover:bg-[#F5EDE5]"
                                        >
                                            Back to Login
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label className="text-[#4A3728]">New Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5D4A3A]" />
                                        <Input
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="Enter new password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="pl-10 pr-10 bg-white border-[#D4BBA6] text-[#4A3728] placeholder:text-[#5D4A3A]/50 focus:border-[#4A3728] focus:ring-[#4A3728]"
                                            required
                                            minLength={6}
                                            data-testid="new-password-input"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5D4A3A] hover:text-[#4A3728]"
                                        >
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                    {password && (
                                        <div className="flex items-center gap-2 mt-2">
                                            <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                                <div 
                                                    className={`h-full ${passwordStrength.color} transition-all`}
                                                    style={{ width: `${(passwordStrength.strength / 5) * 100}%` }}
                                                />
                                            </div>
                                            <span className="text-xs text-[#5D4A3A]">{passwordStrength.label}</span>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="space-y-2">
                                    <Label className="text-[#4A3728]">Confirm Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5D4A3A]" />
                                        <Input
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            placeholder="Confirm new password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            className="pl-10 pr-10 bg-white border-[#D4BBA6] text-[#4A3728] placeholder:text-[#5D4A3A]/50 focus:border-[#4A3728] focus:ring-[#4A3728]"
                                            required
                                            minLength={6}
                                            data-testid="confirm-password-input"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5D4A3A] hover:text-[#4A3728]"
                                        >
                                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                    {confirmPassword && password !== confirmPassword && (
                                        <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                                    )}
                                    {confirmPassword && password === confirmPassword && (
                                        <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                                            <CheckCircle className="w-3 h-3" /> Passwords match
                                        </p>
                                    )}
                                </div>
                                
                                <Button 
                                    type="submit" 
                                    disabled={loading || password !== confirmPassword || password.length < 6}
                                    className="w-full bg-[#4A3728] hover:bg-[#3A2A1E] text-white shadow-md disabled:opacity-50"
                                    data-testid="reset-submit-btn"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Resetting...
                                        </>
                                    ) : (
                                        'Reset Password'
                                    )}
                                </Button>
                                
                                <p className="text-xs text-[#9C8C74] text-center">
                                    Password must be at least 6 characters
                                </p>
                            </form>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
